import { isNil } from '@lucidlink/tscore';
import { webCrypto } from '#web-crypto';
import { arrayToBase64, base64ToArray } from './binary-utils';
import { AES_ENCRYPTION_ALGO_PARAMS, RSA_ENCRYPTION_ALGO_NAME, RSA_SIGNING_ALGO_NAME, } from './constants';
export const aesKeyToArray = async (aesKey) => {
    return webCrypto.subtle.exportKey('raw', aesKey);
};
export const privateKeyToArray = async (privateKey) => {
    return webCrypto.subtle.exportKey('pkcs8', privateKey);
};
export const publicKeyToArray = async (publicKey) => {
    return webCrypto.subtle.exportKey('spki', publicKey);
};
export const cryptoKeyToArray = (cryptoKey) => {
    switch (cryptoKey.type) {
        case 'secret':
            return aesKeyToArray(cryptoKey);
        case 'public':
            return publicKeyToArray(cryptoKey);
        case 'private':
            return privateKeyToArray(cryptoKey);
        default:
            throw new Error(`Unsupported crypto key type: ${cryptoKey.type}`);
    }
};
export const arrayToAesKey = async (buffer, params = AES_ENCRYPTION_ALGO_PARAMS) => {
    if (isNil(params.keyUsages)) {
        params.keyUsages = ['encrypt', 'decrypt'];
    }
    if (isNil(params.extractable)) {
        params.extractable = true;
    }
    return await webCrypto.subtle.importKey('raw', buffer, params.algorithm, params.extractable, params.keyUsages);
};
export const arrayToPrivateKey = (buffer, params) => {
    const getKeyUsages = (algoName) => {
        switch (algoName) {
            case RSA_ENCRYPTION_ALGO_NAME:
                return ['decrypt'];
            case RSA_SIGNING_ALGO_NAME:
                return ['sign'];
            default:
                return [];
        }
    };
    return webCrypto.subtle.importKey('pkcs8', buffer, params.algorithm, params.extractable ?? true, params.keyUsages ?? getKeyUsages(getAlgoName(params.algorithm)));
};
export const arrayToPublicKey = (buffer, params) => {
    const getKeyUsages = (algoName) => {
        switch (algoName) {
            case RSA_ENCRYPTION_ALGO_NAME:
                return ['encrypt'];
            case RSA_SIGNING_ALGO_NAME:
                return ['verify'];
            default:
                return [];
        }
    };
    return webCrypto.subtle.importKey('spki', buffer, params.algorithm, params.extractable ?? true, params.keyUsages ?? getKeyUsages(getAlgoName(params.algorithm)));
};
export const cryptoKeyToJwk = (cryptoKey) => {
    return webCrypto.subtle.exportKey('jwk', cryptoKey);
};
export const jwkToCryptoKey = async (jwk, params) => {
    const getKeyUsages = (algoName) => {
        switch (algoName) {
            case RSA_ENCRYPTION_ALGO_NAME:
                return ['encrypt', 'decrypt'];
            case RSA_SIGNING_ALGO_NAME:
                return ['verify', 'sign'];
            default:
                return [];
        }
    };
    return webCrypto.subtle.importKey('jwk', jwk, params.algorithm, params.extractable ?? true, params.keyUsages ?? getKeyUsages(getAlgoName(params.algorithm)));
};
const getAlgoName = (algorithm) => {
    return typeof algorithm === 'string' ? algorithm : algorithm.name;
};
export const exportAesGcmKeyRaw = (key) => {
    return webCrypto.subtle.exportKey('raw', key);
};
export const exportPrivateCryptoKeyToPem = async (key) => {
    const exported = await webCrypto.subtle.exportKey('pkcs8', key);
    const exportedAsBase64 = arrayToBase64(exported);
    const pemExported = `-----BEGIN PRIVATE KEY-----\n${exportedAsBase64}\n-----END PRIVATE KEY-----`;
    return pemExported;
};
export const exportPublicCryptoKeyToPem = async (key) => {
    const exported = await webCrypto.subtle.exportKey('spki', key);
    const exportedAsBase64 = arrayToBase64(exported);
    const pemExported = `-----BEGIN PUBLIC KEY-----\n${exportedAsBase64}\n-----END PUBLIC KEY-----`;
    return pemExported;
};
export const cryptoKeyToPem = async (key) => {
    if (key.type == 'private') {
        return exportPrivateCryptoKeyToPem(key);
    }
    else if (key.type == 'public') {
        return exportPublicCryptoKeyToPem(key);
    }
    else {
        throw new Error('Unexpected CryptoKey type');
    }
};
export const pemToCryptoKey = (pem, params) => {
    const b64String = pem.replace(/-----[^-]+-----/g, '').replace(/\s+/g, '');
    const buffer = base64ToArray(b64String);
    if (pem.startsWith('-----BEGIN PRIVATE KEY-----')) {
        return arrayToPrivateKey(buffer, params);
    }
    else if (pem.startsWith('-----BEGIN PUBLIC KEY-----')) {
        return arrayToPublicKey(buffer, params);
    }
    else {
        throw new Error('Invalid PEM format');
    }
};
export const derivePublicKey = async (privateKey) => {
    const isForSigning = privateKey.usages.includes('sign') ||
        privateKey.usages.includes('verify');
    const params = {
        algorithm: privateKey.algorithm,
        extractable: privateKey.extractable,
        keyUsages: isForSigning ? ['verify'] : ['encrypt'],
    };
    const jwk = await cryptoKeyToJwk(privateKey);
    if (privateKey.algorithm.name.startsWith('RSA')) {
        const publicKeyJwk = {
            kty: jwk.kty,
            n: jwk.n,
            e: jwk.e,
            alg: jwk.alg,
            ext: jwk.ext,
        };
        return jwkToCryptoKey(publicKeyJwk, params);
    }
    else if (privateKey.algorithm.name.startsWith('ECD')) {
        const publicKeyJwk = {
            kty: jwk.kty,
            crv: jwk.crv,
            x: jwk.x,
            y: jwk.y,
            alg: jwk.alg,
            ext: jwk.ext,
        };
        return jwkToCryptoKey(publicKeyJwk, params);
    }
    else {
        throw new Error('Unexpected CryptoKey type');
    }
};
